import React, { FC } from 'react';
import styled, { css } from 'styled-components/macro';

import Pictorial from 'components/Pictorial';
import Column from 'components/Grid/Column';
import { ColorVariants } from 'theme';
import media from 'helpers/media';

const PictorialStyled = styled(Pictorial)<{
    $orientation: 'horizontal' | 'vertical';
}>`
    margin-bottom: ${({ theme }) => theme.space.base};
    ${({ theme, $orientation }) =>
        $orientation === 'vertical' &&
        css`
            margin-bottom: 0;
            ${media.mobile`
                margin-bottom: ${theme.space.base};
            `}
        `};
`;

const Wrapper = styled.div<{
    $color: ColorVariants;
    $orientation: 'horizontal' | 'vertical';
}>`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;

    margin-left: -${({ theme }) => theme.space.base};

    ${({ theme }) => media.fromTablet`
        margin-left: -${theme.space.medium};
    `}

    &:not(:first-child) {
        margin-top: ${({ theme }) => theme.space.giant};
    }

    ${({ $color, theme }) => `color: ${theme.colors[$color]};`}

    ${({ $orientation, theme }) =>
        $orientation === 'vertical' &&
        css`
            display: flex;
            text-align: left;

            &:not(:first-child) {
                margin-top: ${theme.space.xgiant};
            }
        `};

    .Item_sc {
        text-align: center;
        padding: 0 ${({ theme }) => theme.space.small};

        ${({ $orientation, theme }) =>
            $orientation === 'vertical' &&
            css`
                display: flex;
                text-align: left;

                &:not(:first-child) {
                    margin-top: ${theme.space.xlarge};
                }
            `};
    }

    .Text_sc {
        ${({ $orientation, theme }) =>
            $orientation === 'vertical' &&
            css`
                margin-left: ${theme.space.xlarge};
            `};
    }

    .Name_sc {
        font-size: 1.5rem;
        line-height: 1.8125rem;
    }

    .Description_sc {
        margin-top: ${({ theme }) => theme.space.base};
        color: ${({ theme }) => theme.colors.baseGray};
        font-size: 1.125rem;
        line-height: 1.5rem;
    }

    ${({ theme, $orientation }) => media.mobile`
        &:not(:first-child) {
            margin-top: ${theme.space.medium};
        }

        .Item_sc {
            text-align: center;
            &:not(:first-child) {
                margin-top: ${theme.space.large};
            }
        }

        ${
            $orientation === 'vertical' &&
            css`
                .Item_sc {
                    display: block;
                    text-align: center;

                    &:not(:first-child) {
                        margin-top: ${theme.space.medium};
                    }
                }

                .Text_sc {
                    margin-left: 0;
                }
            `
        };
    `}
`;

type Feature = {
    icon: FC;
    name: string;
    description: string;
};

export type FeaturesProps = {
    /** List of features to display */
    features: Feature[];

    textColor?: ColorVariants;
    pictorialColor?: ColorVariants;

    /** Features orientation */
    orientation?: 'horizontal' | 'vertical';
} & React.HTMLAttributes<HTMLDivElement>;

const Features: FC<FeaturesProps> = ({
    features = [],
    orientation = 'horizontal',
    textColor,
    pictorialColor,
    ...rest
}) => {
    const cols = orientation === 'horizontal' ? 12 / features.length : 12;

    return (
        <Wrapper
            $orientation={orientation}
            $color={textColor ? textColor : 'darkGrey'}
            {...rest}
        >
            {features.map((feature, i) => {
                return (
                    <Column
                        className="Item_sc"
                        cols={cols}
                        tablet={cols === 3 ? 6 : 0}
                        mobile={cols === 3 ? 2 : 0}
                        key={`feature-${i}`}
                    >
                        <PictorialStyled
                            $orientation={orientation}
                            color={pictorialColor ? pictorialColor : 'thumb'}
                            icon={feature.icon}
                        />
                        <div className="Text_sc">
                            <p
                                className="Name_sc"
                                dangerouslySetInnerHTML={{
                                    __html: feature.name,
                                }}
                            />
                            <p
                                className="Description_sc"
                                dangerouslySetInnerHTML={{
                                    __html: feature.description,
                                }}
                            />
                        </div>
                    </Column>
                );
            })}
        </Wrapper>
    );
};

export default Features;
